import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Settings } from '../../types';

interface EquilibreBarProps {
  partner1Name: string;
  partner2Name: string;
  /** Pourcentages réels du split (déjà calculés côté serveur selon le mode). */
  partner1Percentage: number;
  partner2Percentage: number;
  /** Montants à la charge de chaque partenaire sur la période. */
  partner1Share: number;
  partner2Share: number;
  /** Revenus de chaque partenaire (affichés en sous-titre quand disponibles). */
  partner1Income?: number;
  partner2Income?: number;
  calculationMode: Settings['calculationMode'];
  formatCurrency: (amount: number) => string;
}

/**
 * Équilibre des partenaires — panneau compact du hero.
 *
 * Volontairement dimensionné pour tenir dans la colonne droite du hero
 * (barre de 46 px) : c'est un point de lecture, pas une bannière pleine largeur.
 * Les pourcentages viennent du summary serveur, qui applique déjà le vrai mode
 * de calcul (proportionnel / 50·50 / personnalisé / fixe).
 *
 * Couleurs : axe partenaires `--pa` / `--pb`, jamais la sémantique crédit/débit.
 */
export default function EquilibreBar({
  partner1Name,
  partner2Name,
  partner1Percentage,
  partner2Percentage,
  partner1Share,
  partner2Share,
  partner1Income,
  partner2Income,
  calculationMode,
  formatCurrency,
}: EquilibreBarProps) {
  const { t } = useTranslation('dashboard');

  const { p1, p2, gap, verdict } = useMemo(() => {
    const raw1 = Number.isFinite(partner1Percentage) ? partner1Percentage : 0;
    const raw2 = Number.isFinite(partner2Percentage) ? partner2Percentage : 0;
    const sum = raw1 + raw2;
    // Normalisation défensive : jamais de division par zéro, largeur toujours 100 %.
    const n1 = sum > 0 ? (raw1 / sum) * 100 : 50;
    const n2 = sum > 0 ? (raw2 / sum) * 100 : 50;
    const dev = Math.round(Math.abs(n1 - 50));
    return {
      p1: n1,
      p2: n2,
      gap: Math.round(Math.abs(n1 - n2)),
      verdict: dev <= 3 ? 'parity' : dev <= 8 ? 'balanced' : 'unbalanced',
    };
  }, [partner1Percentage, partner2Percentage]);

  return (
    <section className="panel">
      <div className="panel-h">
        <h3>{t('equilibre.title')}</h3>
        <span className="hint">{t(`equilibre.modes.${calculationMode}`, { defaultValue: t('equilibre.modes.custom') })}</span>
      </div>
      <div className="equi">
        <p className="equi-verdict">
          {t('equilibre.verdictPrefix')} <b>{t(`equilibre.verdict.${verdict}`)}</b>
          {gap > 0 && ` · ${t('equilibre.gap', { count: gap })}`}
        </p>

        <div
          className="equi-bar"
          role="img"
          aria-label={`${partner1Name} ${Math.round(p1)} %, ${partner2Name} ${Math.round(p2)} %`}
        >
          <div className="seg-a" style={{ width: `${p1}%` }}>
            <span className="seg-lbl">{partner1Name} {Math.round(p1)} %</span>
          </div>
          <div className="seg-b" style={{ width: `${p2}%` }}>
            <span className="seg-lbl">{partner2Name} {Math.round(p2)} %</span>
          </div>
          {/* Repère de parité 50·50 */}
          <div className="fair" aria-hidden />
        </div>

        <div className="equi-legend">
          <div className="who">
            <span className="dot a">{partner1Name.charAt(0).toUpperCase()}</span>
            <div className="min-w-0">
              <div className="w-name truncate">{partner1Name}</div>
              {partner1Income !== undefined && (
                <div className="w-sub">{t('equilibre.income', { amount: formatCurrency(partner1Income) })}</div>
              )}
            </div>
            <div className="w-amt">
              <b className="num">{formatCurrency(partner1Share)}</b>
              <span>{t('equilibre.hisShare')}</span>
            </div>
          </div>
          <div className="who">
            <span className="dot b">{partner2Name.charAt(0).toUpperCase()}</span>
            <div className="min-w-0">
              <div className="w-name truncate">{partner2Name}</div>
              {partner2Income !== undefined && (
                <div className="w-sub">{t('equilibre.income', { amount: formatCurrency(partner2Income) })}</div>
              )}
            </div>
            <div className="w-amt">
              <b className="num">{formatCurrency(partner2Share)}</b>
              <span>{t('equilibre.hisShare')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
